import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  CalendarIcon,
  ChevronDownIcon,
  BanknotesIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'
import type { HubnaiTransfer } from '@/services/api'
import CreateHubnaiTransferModal from '@/components/payments/CreateHubnaiTransferModal'

const GatewayFinProTransfers: React.FC = () => {
  const navigate = useNavigate()

  const [transfers, setTransfers] = useState<HubnaiTransfer[]>([])
  const [loading, setLoading] = useState(true)
  const [showTransferModal, setShowTransferModal] = useState(false)

  // Filtros
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [periodFilter, setPeriodFilter] = useState(30)
  const [periodLabel, setPeriodLabel] = useState('Últimos 30 dias')
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false)

  // Paginação
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  useEffect(() => {
    loadTransfers()
  }, [statusFilter, periodFilter])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.period-filter-dropdown')) {
        setShowPeriodDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadTransfers = async () => {
    try {
      setLoading(true)

      const params: any = {
        limit: 1000,
        skip: 0
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter
      }

      const response = await hubnaiAPI.listTransfers(params)
      setTransfers(response.data)

    } catch (error: any) {
      console.error('Erro ao carregar transferências:', error)
      alert('Erro ao carregar transferências: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; class: string; icon: any }> = {
      pending: {
        label: 'Pendente',
        class: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: ClockIcon
      },
      processing: {
        label: 'Processando',
        class: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: ArrowUpIcon
      },
      completed: {
        label: 'Concluído',
        class: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircleIcon
      },
      failed: {
        label: 'Falhou',
        class: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircleIcon
      }
    }

    return configs[status] || {
      label: status,
      class: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: ExclamationCircleIcon
    }
  }

  const getStatusBadge = (status: string) => {
    const config = getStatusConfig(status)
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full border ${config.class}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    )
  }

  const getTransferTypeBadge = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      pix: { label: 'PIX', color: 'text-purple-600 bg-purple-50 border-purple-200' },
      ted: { label: 'TED', color: 'text-blue-600 bg-blue-50 border-blue-200' }
    }

    const typeInfo = typeMap[type] || { label: type.toUpperCase(), color: 'text-gray-600 bg-gray-50 border-gray-200' }

    return (
      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${typeInfo.color}`}>
        {typeInfo.label}
      </span>
    )
  }

  const handlePeriodChange = (days: number, label: string) => {
    setPeriodFilter(days)
    setPeriodLabel(label)
    setShowPeriodDropdown(false)
    setCurrentPage(1)
  }

  // Cálculo de paginação
  const totalPages = Math.ceil(transfers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedTransfers = transfers.slice(startIndex, endIndex)

  // Calcular estatísticas
  const completedTransfers = transfers.filter(t => t.status === 'completed')
  const pendingTransfers = transfers.filter(t => t.status === 'pending' || t.status === 'processing')
  const failedTransfers = transfers.filter(t => t.status === 'failed')

  const totalAmount = transfers.reduce((sum, t) => sum + t.amount, 0)
  const completedAmount = completedTransfers.reduce((sum, t) => sum + t.amount, 0)
  const totalFees = transfers.reduce((sum, t) => sum + (t.fee_amount || 0), 0)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/payments/gateway-finpro')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Voltar"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                  <ArrowUpIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Transferências & Saques</h1>
                  <p className="text-sm text-gray-500">Histórico de transferências do Gateway FinPro</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowTransferModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg font-medium"
            >
              <ArrowUpIcon className="w-5 h-5" />
              Nova Transferência
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      <CreateHubnaiTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSuccess={() => {
          setShowTransferModal(false)
          loadTransfers()
        }}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Total</span>
            <BanknotesIcon className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{transfers.length}</p>
          <p className="text-xs text-gray-500 mt-1">{formatCurrency(totalAmount)}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Concluídas</span>
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{completedTransfers.length}</p>
          <p className="text-xs text-gray-500 mt-1">{formatCurrency(completedAmount)}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Pendentes</span>
            <ClockIcon className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{pendingTransfers.length}</p>
          <p className="text-xs text-gray-500 mt-1">Em processamento</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Falhas</span>
            <XCircleIcon className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{failedTransfers.length}</p>
          <p className="text-xs text-gray-500 mt-1">Não processadas</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Taxas</span>
            <CreditCardIcon className="w-5 h-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalFees)}</p>
          <p className="text-xs text-gray-500 mt-1">Total de tarifas</p>
        </div>
      </div>

      {/* Transfers List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Histórico de Transferências</h2>

            {/* Filtro de Período - Dropdown */}
            <div className="relative period-filter-dropdown">
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <CalendarIcon className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{periodLabel}</span>
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              </button>

              {/* Dropdown Menu */}
              {showPeriodDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handlePeriodChange(30, 'Últimos 30 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 30 dias</span>
                      {periodFilter === 30 && periodLabel === 'Últimos 30 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(60, 'Últimos 60 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 60 dias</span>
                      {periodFilter === 60 && periodLabel === 'Últimos 60 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(90, 'Últimos 90 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 90 dias</span>
                      {periodFilter === 90 && periodLabel === 'Últimos 90 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(365, 'Último ano')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Último ano</span>
                      {periodFilter === 365 && periodLabel === 'Último ano' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Filtro de Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos</option>
              <option value="pending">Pendente</option>
              <option value="processing">Processando</option>
              <option value="completed">Concluído</option>
              <option value="failed">Falhou</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          {transfers.length === 0 ? (
            <div className="p-12 text-center">
              <ArrowUpIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Nenhuma transferência encontrada</p>
              <p className="text-sm text-gray-400 mt-2">Solicite um novo saque para começar</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banco</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Taxa</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Líquido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedTransfers.map((transfer) => (
                    <tr
                      key={transfer.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{formatDate(transfer.requested_at)}</div>
                        {transfer.estimated_date && (
                          <div className="text-xs text-gray-500">
                            Prev: {new Date(transfer.estimated_date).toLocaleDateString('pt-BR')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTransferTypeBadge(transfer.transfer_type)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-mono text-xs">
                          {transfer.bank_account.bank_code} •
                          Ag {transfer.bank_account.agency}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          Conta {transfer.bank_account.account}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(transfer.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                        {formatCurrency(transfer.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                        -{formatCurrency(transfer.fee_amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-green-600">
                        {formatCurrency(transfer.net_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Exibindo <span className="font-medium">{startIndex + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(endIndex, transfers.length)}</span> de{' '}
                    <span className="font-medium">{transfers.length}</span> transferências
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        const showPage =
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)

                        if (!showPage) {
                          if (page === 2 && currentPage > 3) {
                            return <span key={page} className="px-2 text-gray-500">...</span>
                          }
                          if (page === totalPages - 1 && currentPage < totalPages - 2) {
                            return <span key={page} className="px-2 text-gray-500">...</span>
                          }
                          return null
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default GatewayFinProTransfers
